import React, { useState, useEffect, useMemo } from 'react';
import { FaTrash, FaCheck, FaMoneyBillWave, FaArrowRight, FaBolt, FaSun, FaMoon, FaExclamationCircle, FaTimes, FaInfoCircle, FaCreditCard } from 'react-icons/fa';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { pricingService, facilitiesService, daysOfWeek, clubPricingService } from '../../services/sportsManagementService';
import { getMembershipPlans, updateMembershipPlans } from '../../services/cardService';
import api from '../../services/api';
import { Modal } from 'antd';
import { toast } from 'react-toastify';
import './SportsManagementStyle.scss';

dayjs.extend(customParseFormat);

const isFitnessRecord = (item) =>
    ((item?.typeName || item?.type || item?.name || item?.facilityName || item?.programType || '')).toLowerCase().includes('fitness');

const PricingManagement = ({ userRegions, fitnessAccess = true }) => {
    const canManage = useMemo(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const role = (user.userType || user.role || 'admin').toString().toLowerCase();
            return role === 'admin' || role === 'sports' || role === 'sporsuperadmin' || role === '99';
        } catch {
            return true;
        }
    }, []);

    // Only sports/admin can edit prices; regionuser can view but not edit
    const canEditPrices = useMemo(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const role = (user.userType || user.role || 'admin').toString().toLowerCase();
            return role === 'admin' || role === 'sports' || role === 'sporsuperadmin' || role === '99';
        } catch {
            return true;
        }
    }, []);

    const [facilities, setFacilities] = useState([]);
    const [selectedFacilityId, setSelectedFacilityId] = useState('');
    const [pricingList, setPricingList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cardApplicationFee, setCardApplicationFee] = useState(50.00);
    const [savingFee, setSavingFee] = useState(false);
    const [membershipPlans, setMembershipPlans] = useState([]);
    const [savingMembershipPlans, setSavingMembershipPlans] = useState(false);

    // Filter
    const [selectedRegionFilter, setSelectedRegionFilter] = useState('');
    const [selectedDayId, setSelectedDayId] = useState(''); // '' means null (Every Day) in Dropdown logic, but we send null to API
    const [selectedSportTypeId, setSelectedSportTypeId] = useState(''); // '' means null (General)

    // Modal
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({
        pricingId: null,
        startTime: '08:00',
        endTime: '09:00',
        price: '',
        dayOfWeek: null,
        sportTypeId: null
    });

    // Tariff states
    const [morningPrice, setMorningPrice] = useState('');
    const [eveningPrice, setEveningPrice] = useState('');
    const [loadingTariff, setLoadingTariff] = useState(false);
    const [showTariffCard, setShowTariffCard] = useState(false);
    const [applyToAllCourts, setApplyToAllCourts] = useState(false);

    // Club Pricing
    const [clubPricePerHour, setClubPricePerHour] = useState('');
    const [clubNightPricePerHour, setClubNightPricePerHour] = useState('');
    const [lanePricePerHour, setLanePricePerHour] = useState('');
    const [clubPricingList, setClubPricingList] = useState([]);
    const [savingClubPrice, setSavingClubPrice] = useState(false);

    useEffect(() => {
        loadFacilities();
        loadCardApplicationFee();
        loadMembershipPlans();
    }, []);

    const loadCardApplicationFee = async () => {
        try {
            const res = await api.get('/api/card/application-fee');
            if (res?.isSuccess && res?.data?.fee) {
                setCardApplicationFee(res.data.fee);
            }
        } catch (error) {
            console.error('Kart başvuru ücreti yüklenemedi:', error);
        }
    };

    const handleSaveCardFee = async () => {
        Modal.confirm({
            title: 'Onay',
            content: 'Kart başvuru ücretini kaydetmek istediğinize emin misiniz?',
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: async () => {
                if (!canEditPrices) {
                    toast.error('Bu işlem için yöneticiniz ile görüşün.');
                    return;
                }
                setSavingFee(true);
                try {
                    const res = await api.put('/api/card/application-fee', { fee: parseFloat(cardApplicationFee) });
                    if (res?.isSuccess) {
                        toast.success('Kart başvuru ücreti güncellendi.');
                        loadCardApplicationFee();
                    } else {
                        toast.error(res?.resultMessage || 'Ücret güncellenemedi.');
                    }
                } catch (error) {
                    console.error('Ücret güncelleme hatası:', error);
                    toast.error('Ücret güncellenemedi.');
                } finally {
                    setSavingFee(false);
                }
            }
        });
    };

    const loadMembershipPlans = async () => {
        try {
            const res = await getMembershipPlans();
            if (res?.isSuccess) {
                const visiblePlans = fitnessAccess ? (res.data || []) : (res.data || []).filter((plan) => !isFitnessRecord(plan));
                const sortedPlans = [...visiblePlans].sort((a, b) => {
                    if (a.programType !== b.programType) return a.programType.localeCompare(b.programType);
                    return (a.sortOrder || 0) - (b.sortOrder || 0);
                });
                setMembershipPlans(sortedPlans);
            }
        } catch (error) {
            console.error('Üyelik paketleri yüklenemedi:', error);
        }
    };

    const handlePlanChange = (planId, field, value) => {
        setMembershipPlans((prev) => prev.map((plan) => {
            if (plan.id !== planId) return plan;
            return {
                ...plan,
                [field]: field === 'isActive' ? value : value
            };
        }));
    };

    const handleSaveMembershipPlans = async () => {
        Modal.confirm({
            title: 'Onay',
            content: 'Kart üyelik paketlerini güncellemek istediğinize emin misiniz?',
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: async () => {
                if (!canEditPrices) {
                    toast.error('Bu işlem için yöneticiniz ile görüşün.');
                    return;
                }

                const invalidPlan = membershipPlans.find((plan) => Number(plan.price) <= 0 || Number(plan.creditCount) <= 0);
                if (invalidPlan) {
                    toast.error('Tüm paketlerde fiyat ve kontör bilgisi pozitif olmalıdır.');
                    return;
                }

                setSavingMembershipPlans(true);
                try {
                    const payload = membershipPlans.map((plan) => ({
                        id: plan.id,
                        price: Number(plan.price),
                        creditCount: Number(plan.creditCount),
                        isActive: !!plan.isActive
                    }));

                    const res = await updateMembershipPlans(payload);
                    if (res?.isSuccess) {
                        toast.success('Üyelik paketleri güncellendi.');
                        loadMembershipPlans();
                    } else {
                        toast.error(res?.resultMessage || 'Üyelik paketleri güncellenemedi.');
                    }
                } catch (error) {
                    console.error('Üyelik paketleri güncelleme hatası:', error);
                    toast.error('Üyelik paketleri güncellenemedi.');
                } finally {
                    setSavingMembershipPlans(false);
                }
            }
        });
    };

    // Club Pricing Functions
    const loadClubPricing = async (facilityId) => {
        try {
            const res = await clubPricingService.getByFacility(facilityId);
            const data = res?.data?.data || res?.data || [];
            setClubPricingList(data);
            if (data.length > 0) {
                setClubPricePerHour(data[0].pricePerHour || '');
                setClubNightPricePerHour(data[0].nightPricePerHour || '');
                setLanePricePerHour(data[0].lanePricePerHour || '');
            } else {
                setClubPricePerHour('');
                setClubNightPricePerHour('');
                setLanePricePerHour('');
            }
        } catch (err) {
            console.error('Kulüp fiyatları yüklenemedi:', err);
        }
    };

    const handleSaveClubPrice = async () => {
        if (!selectedFacilityId || !clubPricePerHour) return;

        Modal.confirm({
            title: 'Onay',
            content: 'Kulüp fiyatını kaydetmek istediğinize emin misiniz?',
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: async () => {
                if (!canEditPrices) {
                    toast.error('Bu işlem için yöneticiniz ile görüşün.');
                    return;
                }
                setSavingClubPrice(true);
                try {
                    const res = await clubPricingService.save({
                        facilityId: Number(selectedFacilityId),
                        pricePerHour: parseFloat(clubPricePerHour),
                        nightPricePerHour: clubNightPricePerHour ? parseFloat(clubNightPricePerHour) : null,
                        lanePricePerHour: lanePricePerHour ? parseFloat(lanePricePerHour) : null,
                        sportTypeId: null
                    });
                    if (res?.data?.isSuccess !== false) {
                        toast.success('Kulüp fiyatı kaydedildi.');
                        loadClubPricing(selectedFacilityId);
                    } else {
                        toast.error(res?.data?.resultMessage || 'Fiyat kaydedilemedi.');
                    }
                } catch (err) {
                    console.error('Club price save error:', err);
                    toast.error('Fiyat kaydedilemedi.');
                } finally {
                    setSavingClubPrice(false);
                }
            }
        });
    };

    useEffect(() => {
        if (selectedFacilityId) {
            loadPricing(selectedFacilityId);
        } else {
            setPricingList([]);
        }
        setSelectedDayId('');
        setSelectedSportTypeId('');
    }, [selectedFacilityId]);

    // Load club pricing when facility changes
    useEffect(() => {
        if (selectedFacilityId) {
            loadClubPricing(selectedFacilityId);
        } else {
            setClubPricingList([]);
            setClubPricePerHour('');
            setClubNightPricePerHour('');
        }
    }, [selectedFacilityId]);


    const loadFacilities = async () => {
        try {
            const res = await facilitiesService.getAll(true);
            if (res?.isSuccess) {
                let fetchedFacilities = res.data;
                if (userRegions && userRegions.length > 0) {
                    const userRole = JSON.parse(localStorage.getItem('user') || '{}').userType?.toLowerCase();
                    if (userRole === 'regionalsports') {
                        const assignedIds = userRegions.map(ur => ur.id);
                        fetchedFacilities = fetchedFacilities.filter(f => assignedIds.includes(f.regionId));
                    }
                }
                if (!fitnessAccess) {
                    fetchedFacilities = fetchedFacilities.filter(f => !isFitnessRecord(f));
                }
                setFacilities(fetchedFacilities);
            }
        } catch (error) {
            console.error('Tesisler yüklenemedi:', error);
        }
    };

    const loadPricing = async (facilityId) => {
        setLoading(true);
        try {
            const res = await pricingService.getByFacility(facilityId);
            if (res?.isSuccess) {
                setPricingList(res.data);
            }
        } catch (error) {
            console.error('Fiyatlandırma yüklenemedi:', error);
        } finally {
            setLoading(false);
        }
    };

    const selectedFacility = useMemo(() => {
        return facilities.find(f => f.facilityId === parseInt(selectedFacilityId));
    }, [facilities, selectedFacilityId]);

    // Calculate effective base price based on selected sport
    const currentBasePrice = useMemo(() => {
        if (!selectedFacility) return 0;
        if (selectedSportTypeId && selectedFacility.availableSports) {
            const sport = selectedFacility.availableSports.find(s => s.typeId == selectedSportTypeId);
            if (sport) return sport.pricePerHour;
        }
        return selectedFacility.basePrice || 0;
    }, [selectedFacility, selectedSportTypeId]);

    // Calculate dynamic text to display for pricing
    const pricingDisplayText = useMemo(() => {
        if (!selectedFacility) return '';

        // Determine relevant pricing rules based on selected sport
        let relevantPricings = pricingList;
        if (selectedSportTypeId) {
            relevantPricings = pricingList.filter(p => p.sportTypeId == selectedSportTypeId);
        } else {
            relevantPricings = pricingList.filter(p => !p.sportTypeId); // General
        }

        if (relevantPricings.length === 0) {
            return `Standart Ücret: ${currentBasePrice} ₺`;
        }

        // Try to identify Morning / Evening rules by their classical definitions
        const morningRules = relevantPricings.filter(p => {
            const start = p.startTime || p.StartTime;
            const end = p.endTime || p.EndTime;
            return start?.startsWith('08:00') && end?.startsWith('19:00');
        });

        const eveningRules = relevantPricings.filter(p => {
            const start = p.startTime || p.StartTime;
            const end = p.endTime || p.EndTime;
            return start?.startsWith('19:00') && (end?.startsWith('00:00') || end?.startsWith('24:00'));
        });

        if (morningRules.length > 0 || eveningRules.length > 0) {
            // Get typical prices (assume first one if multiple days defined, or generic if no day set)
            const mPrice = morningRules.length > 0 ? morningRules[0].price : null;
            const ePrice = eveningRules.length > 0 ? eveningRules[0].price : null;

            let textParts = [];
            if (mPrice !== null) textParts.push(`Gündüz: ${mPrice} ₺`);
            if (ePrice !== null) textParts.push(`Gece: ${ePrice} ₺`);
            
            return textParts.join(' / ');
        }

        // If rules exist but they don't match the strict 08:00-19:00 / 19:00-00:00 pattern
        const uniquePrices = [...new Set(relevantPricings.map(p => p.price))];
        if (uniquePrices.length === 1) {
            return `Standart Ücret: ${uniquePrices[0]} ₺`;
        } else if (uniquePrices.length > 1) {
            return `Değişken Fiyatlar (${Math.min(...uniquePrices)} ₺ - ${Math.max(...uniquePrices)} ₺)`;
        }

        return `Standart Ücret: ${currentBasePrice} ₺`;

    }, [selectedFacility, currentBasePrice, pricingList, selectedSportTypeId]);

    // Helper to compare times
    const getComparableTime = (timeStr, isEnd) => {
        if (!timeStr) return isEnd ? '24:00' : '00:00';
        if (timeStr === '00:00' && isEnd) return '24:00';
        return timeStr;
    };

    const handleSave = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        
        Modal.confirm({
            title: 'Onay',
            content: 'Bu fiyat kuralını kaydetmek istediğinize emin misiniz?',
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: async () => {
                if (!canEditPrices) {
                    toast.error('Bu işlem için yöneticiniz ile görüşün.');
                    return;
                }
                try {
                    const newStart = getComparableTime(modalData.startTime, false);
                    const newEnd = getComparableTime(modalData.endTime, true);

            if (newStart >= newEnd) {
                toast.error('Bitiş saati, başlangıç saatinden sonra olmalıdır.');
                return;
            }

            // Check working hours
            if (selectedFacility) {
                const facilityStart = getComparableTime(selectedFacility.workingHoursStart || '08:00', false);
                const facilityEnd = getComparableTime(selectedFacility.workingHoursEnd || '00:00', true);
                if (newStart < facilityStart || newEnd > facilityEnd) {
                    toast.error(`Girdiğiniz saatler tesisin çalışma saatleri (${selectedFacility.workingHoursStart || '08:00'} - ${selectedFacility.workingHoursEnd || '00:00'}) dışında!`);
                    return;
                }
            }

            // Check overlaps - only exact day+sport matches count as overlap
            // A specific day rule can coexist with a "Her Gün" (null) rule (overrides it)
            const hasOverlap = pricingList.some(p => {
                if (p.pricingId === modalData.pricingId) return false; // exclude self when updating

                const pDay = p.dayOfWeek;
                const mDay = modalData.dayOfWeek;
                const pSport = p.sportTypeId;
                const mSport = modalData.sportTypeId;

                // Only check overlap when EXACT same day and sport
                // (null === null for "Her Gün", specific === specific for same day)
                const exactDayMatch = pDay === mDay;
                const exactSportMatch = pSport === mSport;

                if (exactDayMatch && exactSportMatch) {
                    const rStart = getComparableTime(p.startTime || p.StartTime, false);
                    const rEnd = getComparableTime(p.endTime || p.EndTime, true);

                    return (newStart < rEnd && newEnd > rStart);
                }
                return false;
            });

            if (hasOverlap) {
                toast.error('Girdiğiniz saat aralığı, mevcut kurallardan biriyle çakışıyor!');
                return;
            }

            const finalPrice = modalData.price && modalData.price !== '' ? parseFloat(modalData.price) : currentBasePrice;
            const payload = {
                facilityId: parseInt(selectedFacilityId),
                startTime: modalData.startTime,
                endTime: modalData.endTime,
                price: finalPrice,
                dayOfWeek: modalData.dayOfWeek, // already INT or NULL
                sportTypeId: modalData.sportTypeId
            };

            // If updating? backend 'create' might handle overlap or we delete old one?
            // The previous code only had 'create' and 'delete'. No update.
            // So if ID exists, we might need to delete first or just create new (backend valid logic?)
            // Assuming create overwrites or we delete if pricingId exists.

            if (modalData.pricingId) {
                await pricingService.delete(modalData.pricingId);
            }

            await pricingService.create(payload);

            toast.success('Fiyat kuralı kaydedildi.');
            setShowModal(false);
            loadPricing(selectedFacilityId);
        } catch (error) {
            console.error('Kaydetme hatası:', error);
            toast.error('Hata oluştu: ' + (error.response?.data?.message || 'Bilinmeyen hata'));
        }
            }
        });
    };

    const handleDelete = async () => {
        if (!modalData.pricingId) return;

        Modal.confirm({
            title: 'Onay',
            content: 'Bu özel fiyatı kaldırmak istediğinize emin misiniz?',
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: async () => {
                if (!canEditPrices) {
                    toast.error('Bu işlem için yöneticiniz ile görüşün.');
                    return;
                }
                try {
                    await pricingService.delete(modalData.pricingId);
                    toast.success('Özel fiyat kaldırıldı.');
                    setShowModal(false);
                    loadPricing(selectedFacilityId);
                } catch (error) {
                    console.error('Silme hatası:', error);
                    toast.error('Silinemedi.');
                }
            }
        });
    }

    const handleApplyTariff = async () => {
        if (!morningPrice && !eveningPrice) {
            toast.error('Lütfen en az bir tarife fiyatı giriniz.');
            return;
        }

        const dayFilter = selectedDayId !== '' ? parseInt(selectedDayId) : null;
        const sportFilter = selectedSportTypeId !== '' ? parseInt(selectedSportTypeId) : null;

        // Determine target facilities
        let targetFacilities = [selectedFacility];
        if (applyToAllCourts && selectedFacility) {
            targetFacilities = facilities.filter(f => f.regionId === selectedFacility.regionId);
        }

        const facilityNames = targetFacilities.map(f => f.facilityName).join(', ');
        
        Modal.confirm({
            title: 'Tarife Uygulama Onayı',
            content: `${selectedDayId ? daysOfWeek.find(d => d.id == selectedDayId)?.name : 'Her Gün'} için ${applyToAllCourts ? `tüm kortlara (${facilityNames})` : selectedFacility.facilityName + ' tesisine'} tarife uygulanacak. Mevcut saat kuralları silinip yeniden oluşturulacak. Devam etmek istiyor musunuz?`,
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: async () => {
                if (!canEditPrices) {
                    toast.error('Bu işlem için yöneticiniz ile görüşün.');
                    return;
                }
                setLoadingTariff(true);
                try {
                    for (const facility of targetFacilities) {
                        const facId = facility.facilityId;

                        // 1. Load this facility's pricing rules
                        let facPricingList = pricingList;
                        if (applyToAllCourts && facId !== parseInt(selectedFacilityId)) {
                            const res = await pricingService.getByFacility(facId);
                            facPricingList = res?.isSuccess ? res.data : [];
                        }

                        // 2. Delete existing rules for this day+sport filter
                        const toDelete = facPricingList.filter(p => {
                            const matchesDay = dayFilter !== null ? p.dayOfWeek === dayFilter : p.dayOfWeek === null;
                            const matchesSport = sportFilter !== null ? p.sportTypeId === sportFilter : p.sportTypeId === null;
                            return matchesDay && matchesSport;
                        });

                        for (const rule of toDelete) {
                            await pricingService.delete(rule.pricingId);
                        }

                        // 3. Determine facility working hours and duration
                        const workStart = facility.workingHoursStart || facility.WorkingHoursStart || '08:00';
                        const workEnd = facility.workingHoursEnd || facility.WorkingHoursEnd || '00:00';
                        const duration = parseInt(facility.slotDurationInMinutes || facility.SlotDurationInMinutes || 60, 10);

                        const [wsH, wsM] = workStart.split(':').map(Number);
                        const [weH_orig, weM_orig] = workEnd.split(':').map(Number);

                        let startTotalMinutes = wsH * 60 + wsM;
                        let endTotalMinutes = weH_orig * 60 + weM_orig;
                        if (endTotalMinutes <= startTotalMinutes) endTotalMinutes += 24 * 60;

                        const tariffBoundaryMinutes = 19 * 60; // 19:00 is the boundary

                        // 4. Generate and Create Slots
                        for (let m = startTotalMinutes; m < endTotalMinutes; m += duration) {
                            const currentSlotStart = m;
                            const currentSlotEnd = m + duration;

                            // If the slot would go beyond working hours, we stop (or we could create a partial slot, 
                            // but usually slots must be full duration for the system)
                            if (currentSlotEnd > endTotalMinutes) break;

                            const sH = Math.floor(currentSlotStart / 60) % 24;
                            const sM = currentSlotStart % 60;
                            const eH = Math.floor(currentSlotEnd / 60) % 24;
                            const eMn = currentSlotEnd % 60;

                            const startTimeStr = `${String(sH).padStart(2, '0')}:${String(sM).padStart(2, '0')}`;
                            const endTimeStr = (eH === 0 && eMn === 0) ? "00:00" : `${String(eH).padStart(2, '0')}:${String(eMn).padStart(2, '0')}`;

                            // Determine price based on boundary (if slot starts before 19:00, use morning price)
                            let slotPrice = null;
                            if (currentSlotStart < tariffBoundaryMinutes) {
                                if (morningPrice) slotPrice = parseFloat(morningPrice);
                            } else {
                                if (eveningPrice) slotPrice = parseFloat(eveningPrice);
                            }

                            if (slotPrice !== null) {
                                await pricingService.create({
                                    facilityId: facId,
                                    startTime: startTimeStr,
                                    endTime: endTimeStr,
                                    price: slotPrice,
                                    dayOfWeek: dayFilter,
                                    sportTypeId: sportFilter
                                });
                            }
                        }
                    }
                    toast.success(`Tarife ${applyToAllCourts ? targetFacilities.length + ' tesise' : 'başarıyla'} uygulandı!`);
                    loadPricing(selectedFacilityId);
                } catch (error) {
                    console.error('Tarife uygulama hatası:', error);
                    toast.error('Tarife uygulanırken hata oluştu.');
                } finally {
                    setLoadingTariff(false);
                }
            }
        });
    };

    return (
        <div className="sports-management-page">
            <div className="page-header">
                <h1><FaMoneyBillWave /> Özel Fiyatlandırma</h1>
            </div>

            {/* Card Application Fee Section */}
            <div className="premium-card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                        <h3 style={{ fontSize: '1.1rem', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FaCreditCard /> Kart Başvuru Ücreti
                        </h3>
                    </div>
                    <div className="card-body" style={{ padding: '1.25rem 1.5rem' }}>
                        <div className="card-fee-wrapper">
                            <div className="form-group" style={{ marginBottom: 0, flex: '1', minWidth: '200px', maxWidth: '400px' }}>
                                <label>Başvuru Ücreti (TL)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={cardApplicationFee}
                                    onChange={(e) => setCardApplicationFee(e.target.value)}
                                    placeholder="50.00"
                                    disabled={!canEditPrices}
                                    style={{ background: canEditPrices ? '#fff' : '#f1f5f9', cursor: canEditPrices ? 'text' : 'not-allowed' }}
                                />
                                <small style={{ color: '#718096', display: 'block', marginTop: '6px', fontSize: '0.8rem' }}>
                                    Yeni kart başvurularında tahsil edilecek ücret
                                </small>
                            </div>
                            <button
                                className="btn-premium btn-success save-btn"
                                onClick={handleSaveCardFee}
                                disabled={savingFee}
                            >
                                <FaCheck /> {savingFee ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </div>
                </div>

            <div className="premium-card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                    <h3 style={{ fontSize: '1.1rem', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaBolt /> Kart Üyelik Paketleri
                    </h3>
                </div>
                <div className="card-body" style={{ padding: '1.25rem 1.5rem' }}>
                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                        {['Fitness', 'Pool'].map((programType) => {
                            const plans = membershipPlans.filter((plan) => plan.programType === programType);
                            if (plans.length === 0) return null;

                            return (
                                <div key={programType} style={{ border: '1px solid #e2e8f0', borderRadius: '14px', padding: '1rem 1.1rem', background: '#fff' }}>
                                    <div style={{ marginBottom: '0.9rem' }}>
                                        <h4 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>{programType === 'Fitness' ? 'Fitness Paketleri' : 'Havuz Paketleri'}</h4>
                                        <small style={{ color: '#64748b' }}>
                                            Kart başvurusu sonrası ve yenileme ekranında kullanılacak paketler.
                                        </small>
                                    </div>

                                    <div style={{ display: 'grid', gap: '0.9rem' }}>
                                        {plans.map((plan) => (
                                            <div key={plan.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1.2fr) 1fr 1fr auto', gap: '12px', alignItems: 'end', padding: '0.9rem', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                <div>
                                                    <div style={{ fontWeight: '700', color: '#1e293b' }}>{plan.displayName}</div>
                                                    <small style={{ color: '#64748b' }}>{programType === 'Fitness' ? 'Fitness kontör paketi' : 'Kart üyelik paketi'}</small>
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label>Fiyat (TL)</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={plan.price}
                                                        onChange={(e) => handlePlanChange(plan.id, 'price', e.target.value)}
                                                        disabled={!canEditPrices}
                                                    />
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label>Kontör</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        value={plan.creditCount}
                                                        onChange={(e) => handlePlanChange(plan.id, 'creditCount', e.target.value)}
                                                        disabled={!canEditPrices}
                                                    />
                                                </div>
                                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', paddingBottom: '10px', fontWeight: '600', color: '#475569' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!plan.isActive}
                                                        onChange={(e) => handlePlanChange(plan.id, 'isActive', e.target.checked)}
                                                        disabled={!canEditPrices}
                                                    />
                                                    Aktif
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            className="btn-premium btn-success save-btn"
                            onClick={handleSaveMembershipPlans}
                            disabled={savingMembershipPlans}
                        >
                            <FaCheck /> {savingMembershipPlans ? 'Kaydediliyor...' : 'Paketleri Kaydet'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="premium-card">
                <div className="card-header">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>

                        {/* Control Bar */}
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div className="form-group" style={{ marginBottom: 0, minWidth: '300px' }}>
                                <label style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '4px' }}>Tesis Seçimi</label>
                                <select
                                    value={selectedFacilityId}
                                    onChange={(e) => setSelectedFacilityId(e.target.value)}
                                >
                                    <option value="">İşlem Yapılacak Tesisi Seçiniz</option>
                                    {facilities.map(f => (
                                        <option key={f.facilityId} value={f.facilityId}>{f.facilityName} ({f.regionName})</option>
                                    ))}
                                </select>
                            </div>

                            {selectedFacilityId && (
                                <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
                                    <label style={{ fontSize: '0.85rem', marginBottom: '4px' }}>Gün Seçimi</label>
                                    <select
                                        value={selectedDayId}
                                        onChange={(e) => setSelectedDayId(e.target.value)}
                                    >
                                        <option value="">Her Gün (Genel Kural)</option>
                                        {daysOfWeek.map(d => (
                                            <option key={d.id} value={d.id}>{d.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {selectedFacility && selectedFacility.isSharedFacility && (
                                <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
                                    <label style={{ fontSize: '0.85rem', marginBottom: '4px' }}>Spor Türü</label>
                                    <select
                                        value={selectedSportTypeId}
                                        onChange={(e) => setSelectedSportTypeId(e.target.value)}
                                    >
                                        <option value="">Genel (Tüm Sporlar)</option>
                                        {selectedFacility.availableSports?.map(s => (
                                            <option key={s.typeId} value={s.typeId}>{s.typeName}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {selectedFacility && (
                            <div className="info-box" style={{ fontSize: '0.9rem', color: '#666', background: '#f8f9fc', padding: '10px', borderRadius: '8px', border: '1px solid #e3e6f0' }}>
                                <FaInfoCircle style={{ marginRight: '6px', color: '#237CB2' }} />
                                <strong>{selectedFacility.facilityName}</strong> için güncel fiyat bilgisi: <strong>{pricingDisplayText}</strong>.
                                <br />
                                Çalışma Saatleri: <strong>{selectedFacility.workingHoursStart || '08:00'} - {selectedFacility.workingHoursEnd || '00:00'}</strong>.
                                <br />
                                Aşağıdan saat dilimlerine tıklayarak özel fiyat atayabilirsiniz.
                            </div>
                        )}

                        {/* Tarife Ayarları */}
                        {selectedFacility && (
                            <div style={{ marginTop: '16px' }}>
                                <button
                                    className="btn-premium btn-outline"
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0.6rem 1.2rem' }}
                                    onClick={() => setShowTariffCard(!showTariffCard)}
                                >
                                    <FaBolt style={{ color: '#eab308' }} />
                                    Tarife Uygula
                                    <span style={{ fontSize: '0.8rem', marginLeft: '4px' }}>{showTariffCard ? '▲' : '▼'}</span>
                                </button>

                                {showTariffCard && (
                                    <div style={{
                                        marginTop: '12px',
                                        padding: '0',
                                        background: '#fff',
                                        border: '1px solid #e3e6f0',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                        zIndex: 1000,
                                        maxHeight: '80vh',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        {/* Header */}
                                        <div style={{
                                            background: '#f8fafc',
                                            borderBottom: '1px solid #e2e8f0',
                                            padding: '16px 20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexShrink: 0
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <FaBolt style={{ color: '#eab308' }} />
                                                <strong style={{ color: '#0f172a', fontSize: '1rem' }}>Sabah / Akşam Tarifesi</strong>
                                            </div>
                                            <span style={{ fontSize: '0.8rem', color: '#1d4ed8', background: '#dbeafe', padding: '4px 12px', borderRadius: '50px', fontWeight: '600', border: '1px solid #bfdbfe' }}>
                                                {selectedDayId ? daysOfWeek.find(d => d.id == selectedDayId)?.name : 'Her Gün'}
                                            </span>
                                        </div>

                                        {/* Scrollable Body */}
                                    <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                                                {/* Sabah */}
                                                <div style={{
                                                    padding: '16px',
                                                    borderRadius: '10px',
                                                    border: '2px solid #e3e6f0',
                                                    background: '#fcfcfd',
                                                    transition: 'all 0.2s'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                                                    <div style={{
                                                        width: '36px', height: '36px', borderRadius: '10px',
                                                        background: '#fef3c7', color: '#d97706',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '1.1rem',
                                                        boxShadow: '0 2px 4px rgba(217, 119, 6, 0.1)'
                                                    }}>
                                                        <FaSun size={18} />
                                                    </div>
                                                        <div>
                                                            <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>Sabah Tarifesi</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#718096' }}>08:00 - 19:00</div>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={morningPrice}
                                                            onChange={(e) => setMorningPrice(e.target.value)}
                                                            placeholder="Fiyat giriniz"
                                                            disabled={false}
                                                            style={{
                                                                flex: 1, padding: '10px 12px', borderRadius: '8px',
                                                                border: '1px solid #d1d3e2', fontSize: '1rem',
                                                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                                                outline: 'none',
                                                                background: '#fff',
                                                                cursor: 'text'
                                                            }}
                                                            onFocus={(e) => { e.target.style.borderColor = '#237CB2'; e.target.style.boxShadow = '0 0 0 3px rgba(35,124,178,0.15)'; }}
                                                            onBlur={(e) => { e.target.style.borderColor = '#d1d3e2'; e.target.style.boxShadow = 'none'; }}
                                                        />
                                                        <span style={{ fontWeight: '700', color: '#237CB2', fontSize: '1rem' }}>₺</span>
                                                    </div>
                                                </div>

                                                {/* Akşam */}
                                                <div style={{
                                                    padding: '16px',
                                                    borderRadius: '10px',
                                                    border: '2px solid #e3e6f0',
                                                    background: '#fcfcfd',
                                                    transition: 'all 0.2s'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                                                    <div style={{
                                                        width: '36px', height: '36px', borderRadius: '10px',
                                                        background: '#f3e8ff', color: '#7e22ce',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '1.1rem',
                                                        boxShadow: '0 2px 4px rgba(126, 34, 206, 0.1)'
                                                    }}>
                                                        <FaMoon size={18} />
                                                    </div>
                                                        <div>
                                                            <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>Akşam Tarifesi</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#718096' }}>19:00 - 00:00</div>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={eveningPrice}
                                                            onChange={(e) => setEveningPrice(e.target.value)}
                                                            placeholder="Fiyat giriniz"
                                                            disabled={false}
                                                            style={{
                                                                flex: 1, padding: '10px 12px', borderRadius: '8px',
                                                                border: '1px solid #d1d3e2', fontSize: '1rem',
                                                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                                                outline: 'none',
                                                                background: '#fff',
                                                                cursor: 'text'
                                                            }}
                                                            onFocus={(e) => { e.target.style.borderColor = '#237CB2'; e.target.style.boxShadow = '0 0 0 3px rgba(35,124,178,0.15)'; }}
                                                            onBlur={(e) => { e.target.style.borderColor = '#d1d3e2'; e.target.style.boxShadow = 'none'; }}
                                                        />
                                                        <span style={{ fontWeight: '700', color: '#237CB2', fontSize: '1rem' }}>₺</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Fixed Bottom Section (Toggle + Footer) */}
                                        <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
                                            {/* Tüm Kortlar toggle */}
                                            <div style={{
                                                display: 'flex', alignItems: 'center', gap: '10px',
                                                marginBottom: '16px', padding: '10px 14px',
                                                background: '#f8f9fc', borderRadius: '8px',
                                                border: '1px solid #e3e6f0'
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    id="applyAllCourts"
                                                    checked={applyToAllCourts}
                                                    onChange={(e) => setApplyToAllCourts(e.target.checked)}
                                                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#237CB2' }}
                                                />
                                                <label htmlFor="applyAllCourts" style={{ cursor: 'pointer', fontWeight: '500', color: '#2d3748', margin: 0, fontSize: '0.9rem' }}>
                                                    Tüm Tesislere Uygula
                                                    <span style={{ fontWeight: '400', color: '#718096', fontSize: '0.8rem', marginLeft: '6px' }}>
                                                        ({selectedFacility ? facilities.filter(f => f.regionId === selectedFacility.regionId).length : 0} tesis - {selectedFacility?.regionName})
                                                    </span>
                                                </label>
                                            </div>

                                            {/* Footer */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '20px',
                                                padding: '16px',
                                                background: '#f8fafc',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                flexWrap: 'wrap-reverse'
                                            }}>
                                                <div style={{ flex: 1, minWidth: '200px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                                    <FaExclamationCircle style={{ color: '#64748b', marginTop: '3px', flexShrink: 0 }} />
                                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', lineHeight: '1.4' }}>
                                                        Tarife uygulandığında, seçili gün ve spor türü için mevcut tüm saat kuralları silinip yeniden oluşturulacaktır.
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={handleApplyTariff}
                                                    disabled={loadingTariff || (!morningPrice && !eveningPrice)}
                                                    className="btn-premium"
                                                    style={{ 
                                                        minWidth: '160px', 
                                                        height: '48px', 
                                                        margin: 0,
                                                        backgroundColor: (loadingTariff || (!morningPrice && !eveningPrice)) ? '#e2e8f0' : '#0f75bc',
                                                        color: (loadingTariff || (!morningPrice && !eveningPrice)) ? '#94a3b8' : '#ffffff',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        fontWeight: '700',
                                                        fontSize: '1rem',
                                                        boxShadow: (loadingTariff || (!morningPrice && !eveningPrice)) ? 'none' : '0 4px 12px rgba(15, 117, 188, 0.3)',
                                                        cursor: (loadingTariff || (!morningPrice && !eveningPrice)) ? 'not-allowed' : 'pointer',
                                                        transition: 'all 0.2s ease-in-out'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!loadingTariff && (morningPrice || eveningPrice)) {
                                                            e.currentTarget.style.backgroundColor = '#0c5c96';
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!loadingTariff && (morningPrice || eveningPrice)) {
                                                            e.currentTarget.style.backgroundColor = '#0f75bc';
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                        }
                                                    }}
                                                >
                                                    {loadingTariff ? 'Uygulanıyor...' : 'Tarifeyi Uygula'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {selectedFacilityId && (
                    <div className="card-body">
                        {loading ? (
                            <div className="loading-spinner">Yükleniyor...</div>
                        ) : (
                            <>
                                <div className="responsive-pricing-header">
                                    <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: 0, color: '#2d3748' }}>
                                        {selectedDayId ? daysOfWeek.find(d => d.id == selectedDayId)?.name : 'Her Gün'}
                                        {selectedSportTypeId && selectedFacility.availableSports
                                            ? ` - ${selectedFacility.availableSports.find(s => s.typeId == selectedSportTypeId)?.typeName}`
                                            : ''} İçin Saatler
                                    </h4>
                                    <button
                                        className="btn-premium btn-success"
                                        style={{ padding: '0.4rem 1rem', fontSize: '0.9rem', marginBottom: 0 }}
                                        onClick={() => {
                                            const dayFilter = selectedDayId !== '' ? parseInt(selectedDayId) : null;
                                            const sportFilter = selectedSportTypeId !== '' ? parseInt(selectedSportTypeId) : null;

                                            // Auto-fill: find last slot in current filter context
                                            const filteredSlots = pricingList
                                                .filter(p => {
                                                    const matchesDay = selectedDayId ? p.dayOfWeek === parseInt(selectedDayId) : p.dayOfWeek === null;
                                                    const matchesSport = selectedSportTypeId ? p.sportTypeId === parseInt(selectedSportTypeId) : p.sportTypeId === null;
                                                    return matchesDay && matchesSport;
                                                })
                                                .sort((a, b) => (a.endTime || a.EndTime || '').localeCompare(b.endTime || b.EndTime || ''));

                                            let newStart = '08:00';
                                            let newEnd = '09:00';

                                            if (filteredSlots.length > 0) {
                                                const lastSlot = filteredSlots[filteredSlots.length - 1];
                                                const lastEnd = (lastSlot.endTime || lastSlot.EndTime || '09:00').substring(0, 5);
                                                const lastStart = (lastSlot.startTime || lastSlot.StartTime || '08:00').substring(0, 5);

                                                // Calculate duration of last slot
                                                const [sh, sm] = lastStart.split(':').map(Number);
                                                const [eh, em] = lastEnd.split(':').map(Number);
                                                let durationMins = (eh * 60 + em) - (sh * 60 + sm);
                                                if (durationMins <= 0) durationMins = 60;

                                                // New slot starts where last one ended
                                                newStart = lastEnd;
                                                const [nh, nm] = newStart.split(':').map(Number);
                                                const totalMins = nh * 60 + nm + durationMins;
                                                const endH = Math.floor(totalMins / 60);
                                                const endM = totalMins % 60;
                                                newEnd = endH >= 24 ? '00:00' : `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                                            }

                                            setModalData({
                                                pricingId: null,
                                                startTime: newStart,
                                                endTime: newEnd,
                                                price: '',
                                                dayOfWeek: dayFilter,
                                                sportTypeId: sportFilter
                                            });
                                            setShowModal(true);
                                        }}
                                    >
                                        + Yeni Saat Aralığı Ekle
                                    </button>
                                </div>

                                <div className="custom-pricing-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
                                    {pricingList
                                        .filter(p => {
                                            const matchesDay = selectedDayId ? p.dayOfWeek === parseInt(selectedDayId) : p.dayOfWeek === null;
                                            const matchesSport = selectedSportTypeId ? p.sportTypeId === parseInt(selectedSportTypeId) : p.sportTypeId === null;
                                            return matchesDay && matchesSport;
                                        })
                                        .sort((a, b) => {
                                            const startA = (a.startTime || a.StartTime || '00:00');
                                            const startB = (b.startTime || b.StartTime || '00:00');
                                            return startA.localeCompare(startB);
                                        })
                                        .map((pricing, index) => {
                                            const startTime = (pricing.startTime || pricing.StartTime).substring(0, 5);
                                            const endTime = (pricing.endTime || pricing.EndTime).substring(0, 5);

                                            return (
                                                <div
                                                    key={pricing.pricingId || index}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: '12px 8px',
                                                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                                        border: '2px solid #bfdbfe',
                                                        borderRadius: '10px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        gap: '4px'
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'; e.currentTarget.style.borderColor = '#60a5fa'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(37,99,235,0.2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'; e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                                    onClick={() => {
                                                        const dayFilter = selectedDayId !== '' ? parseInt(selectedDayId) : null;
                                                        const sportFilter = selectedSportTypeId !== '' ? parseInt(selectedSportTypeId) : null;
                                                        setModalData({
                                                            pricingId: pricing.pricingId,
                                                            startTime: startTime,
                                                            endTime: endTime,
                                                            price: pricing.price,
                                                            dayOfWeek: pricing.dayOfWeek,
                                                            sportTypeId: pricing.sportTypeId,
                                                            isInherited: pricing.sportTypeId !== sportFilter
                                                        });
                                                        setShowModal(true);
                                                    }}
                                                >
                                                    <strong style={{ fontSize: '0.95rem', color: '#1e293b' }}>{startTime} - {endTime}</strong>
                                                    <strong style={{ fontSize: '1rem', color: '#16a34a' }}>{pricing.price} ₺</strong>
                                                </div>
                                            );
                                        })}

                                    {pricingList.filter(p => {
                                        const matchesDay = selectedDayId ? p.dayOfWeek === parseInt(selectedDayId) : p.dayOfWeek === null;
                                        const matchesSport = selectedSportTypeId ? p.sportTypeId === parseInt(selectedSportTypeId) : p.sportTypeId === null;
                                        return matchesDay && matchesSport;
                                    }).length === 0 && (
                                            <div style={{ padding: '2rem', textAlign: 'center', color: '#718096', background: '#f8f9fa', borderRadius: '8px', border: '1px dashed #cbd5e0', gridColumn: '1 / -1' }}>
                                                Bu kriterlere uygun özel bir saat kuralı bulunmamaktadır.<br />
                                                Yukarıdaki "Yeni Saat Aralığı Ekle" butonunu kullanarak ekleyebilirsiniz.
                                            </div>
                                        )}
                                </div>


                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '450px' }}>
                        <div className="modal-header">
                            <h2>{modalData.pricingId ? 'Saat Yapılandırmasını Düzenle' : 'Özel Saat Ekle'}</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Saat Aralığı</label>
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', border: '1px solid #d9d9d9', borderRadius: '8px', padding: '0 8px' }}>
                                            <TimePicker
                                                value={modalData.startTime ? dayjs(modalData.startTime, 'HH:mm') : null}
                                                format="HH:mm"
                                                minuteStep={15}
                                                showNow={false}
                                                needConfirm={false}
                                                allowClear={false}
                                                onChange={(time, timeString) => setModalData({ ...modalData, startTime: timeString })}
                                                style={{ width: '120px', height: '40px', background: 'transparent' }}
                                            />
                                            <span style={{ fontWeight: 'bold', color: '#718096' }}>-</span>
                                            <TimePicker
                                                value={modalData.endTime ? dayjs(modalData.endTime, 'HH:mm') : null}
                                                format="HH:mm"
                                                minuteStep={15}
                                                showNow={false}
                                                needConfirm={false}
                                                allowClear={false}
                                                onChange={(time, timeString) => setModalData({ ...modalData, endTime: timeString })}
                                                style={{ width: '120px', height: '40px', background: 'transparent' }}
                                            />
                                        </div>
                                    </div>
                                    <small style={{ color: '#718096', display: 'block', marginTop: '4px' }}>
                                        Birden fazla saat dilimini kapsayacak şekilde bitiş saatini seçebilirsiniz.
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Ücret (TL)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={modalData.price}
                                        onChange={(e) => setModalData({ ...modalData, price: e.target.value })}
                                        placeholder={`Örn: ${currentBasePrice} (Boş bırakılırsa standart fiyat atanır)`}
                                        autoFocus
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Geçerli Kural</label>
                                    <input
                                        type="text"
                                        disabled
                                        value={
                                            (modalData.dayOfWeek !== null ? daysOfWeek.find(d => d.id === modalData.dayOfWeek)?.name : 'Her Gün') +
                                            (modalData.sportTypeId ? ` - ${selectedFacility.availableSports?.find(s => s.typeId == modalData.sportTypeId)?.typeName}` : '')
                                        }
                                        style={{ background: '#f1f5f9' }}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                {modalData.pricingId && (
                                    <button type="button" className="btn-premium btn-danger" onClick={handleDelete} style={{ marginRight: 'auto' }}>
                                        <FaTrash /> Kaldır
                                    </button>
                                )}
                                <button type="button" className="btn-premium btn-outline" onClick={() => setShowModal(false)}>İptal</button>
                                <button type="submit" className="btn-premium btn-success">
                                    <FaCheck /> Kaydet
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Club Pricing Section */}
            {selectedFacility && canManage && !(selectedFacility.typeName || '').toLowerCase().includes('fitness') && (
                <div className="pricing-card" style={{ marginTop: '2rem' }}>
                    <div className="pricing-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FaMoneyBillWave style={{ color: '#6f42c1' }} />
                            <h3 style={{ margin: 0 }}>Kulüp Fiyatı</h3>
                        </div>
                    </div>
                    <div className="pricing-card-body">
                        <p style={{ color: '#64748b', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
                            Bu tesis için kulüp saatlik ücretini belirleyin. Bu fiyat kulüplerin ödeme sayfasında gösterilecektir.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Gündüz Kulüp Ücreti (TL)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={clubPricePerHour}
                                    onChange={(e) => setClubPricePerHour(e.target.value)}
                                    placeholder="Örn: 200"
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem 0.75rem',
                                        border: '1px solid #d1d3e2',
                                        borderRadius: '8px',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Gece Kulüp Ücreti (TL) - 19:00 ve Sonrası</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={clubNightPricePerHour}
                                    onChange={(e) => setClubNightPricePerHour(e.target.value)}
                                    placeholder="Örn: 250 (Boşsa gündüz fiyatı geçerli olur)"
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem 0.75rem',
                                        border: '1px solid #d1d3e2',
                                        borderRadius: '8px',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                            <button
                                onClick={handleSaveClubPrice}
                                disabled={savingClubPrice || !clubPricePerHour}
                                className="btn-premium btn-success"
                                style={{ whiteSpace: 'nowrap', width: '100%', maxWidth: '200px' }}
                            >
                                <FaCheck /> {savingClubPrice ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                        {clubPricingList.length > 0 && (
                            <div style={{ marginTop: '1.25rem', padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', color: '#334155', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                <div><strong style={{ color: '#0f172a' }}>Mevcut Fiyat (Gündüz):</strong> <span style={{ color: '#059669', fontWeight: '600' }}>{clubPricingList[0].pricePerHour?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span></div>
                                {clubPricingList[0].nightPricePerHour > 0 && (
                                    <div><strong style={{ color: '#0f172a' }}>Mevcut Fiyat (Gece):</strong> <span style={{ color: '#059669', fontWeight: '600' }}>{clubPricingList[0].nightPricePerHour?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span></div>
                                )}
                                {clubPricingList[0].lanePricePerHour > 0 && (
                                    <div><strong style={{ color: '#0f172a' }}>Kulvar Fiyatı:</strong> <span style={{ color: '#059669', fontWeight: '600' }}>{clubPricingList[0].lanePricePerHour?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span><span style={{ fontSize: '0.8rem', color: '#64748b' }}>/kulvar</span></div>
                                )}
                            </div>
                        )}

                        {/* Lane Pricing - Only for pool facilities */}
                        {selectedFacility && (selectedFacility.typeName || '').toLowerCase().includes('havuz') && (
                            <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <h4 style={{ margin: '0 0 1rem', color: '#0f172a', fontSize: '1rem', fontWeight: '600' }}>Kulvar Fiyatlandırması (Havuz)</h4>
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Kulvar Başı Saatlik Ücret (TL)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={lanePricePerHour}
                                            onChange={(e) => setLanePricePerHour(e.target.value)}
                                            placeholder="Örn: 750"
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem 0.75rem',
                                                border: '1px solid #7dd3fc',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                background: '#fff'
                                            }}
                                        />
                                    </div>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#0369a1' }}>
                                    <strong>Kulvar Hesaplama Tablosu:</strong>
                                    <table style={{ width: '100%', marginTop: '0.5rem', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                        <thead>
                                            <tr style={{ background: '#e0f2fe' }}>
                                                <th style={{ padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid #bae6fd' }}>Kişi Sayısı</th>
                                                <th style={{ padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid #bae6fd' }}>Kulvar</th>
                                                <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid #bae6fd' }}>Saatlik Ücret</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { range: '1 - 12 (+1)', lanes: 1 },
                                                { range: '13 - 24 (+1)', lanes: 2 },
                                                { range: '25 - 36 (+1)', lanes: 3 },
                                                { range: '37 - 48 (+1)', lanes: 4 },
                                                { range: '49 - 60 (+1)', lanes: 5 }
                                            ].map((row, idx) => (
                                                <tr key={idx} style={{ background: idx % 2 === 0 ? '#f0f9ff' : '#fff' }}>
                                                    <td style={{ padding: '0.35rem 0.4rem' }}>{row.range} kişi</td>
                                                    <td style={{ padding: '0.35rem 0.4rem' }}>{row.lanes} Kulvar</td>
                                                    <td style={{ padding: '0.35rem 0.4rem', textAlign: 'right', fontWeight: '600' }}>
                                                        {lanePricePerHour ? (parseFloat(lanePricePerHour) * row.lanes).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '—'} TL
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PricingManagement;
